import React, { useState, useEffect, useRef } from "react";
import { X, Send, Loader, MessageCircle } from "lucide-react";
import { Query, ID } from "appwrite";
import { useStateValue } from "../../../StateProvider";
import {
  tablesDB,
  DB_ID,
  CHATS_TABLE,
  MESSAGES_TABLE,
} from "../../../appwrite";
import "./ChatWindow.css";
import tbLogo from "../../../Components/Assets/images/tb_logo.png";

const ADMIN_SENDER = "support@trendbayds.com";
const POLL_MS = 2000;

const isOnline = () => {
  const h = new Date().getHours();
  return h >= 8 && h < 20;
};

export default function ChatWindow({ visible, onClose }) {
  const [{ user }] = useStateValue();
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef();
  const pollRef = useRef(null);
  const pendingIds = useRef(new Set());

  // Get or create chat when window opens
  useEffect(() => {
    if (!visible || !user?.email) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: CHATS_TABLE,
          queries: [Query.equal("userEmail", user.email), Query.limit(1)],
        });
        const existing = (res.rows || [])[0];
        let id;
        if (existing) {
          id = existing.$id;
        } else {
          const created = await tablesDB.createRow({
            databaseId: DB_ID,
            tableId: CHATS_TABLE,
            rowId: ID.unique(),
            data: {
              userEmail: user.email,
              participants: [user.email, ADMIN_SENDER],
              lastMessageAt: new Date().toISOString(),
            },
          });
          id = created.$id;
        }
        if (!cancelled) setChatId(id);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, user?.email]);

  // Poll for messages every 2s while window is open
  useEffect(() => {
    if (!chatId || !visible) return;

    const fetchMessages = async () => {
      try {
        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: MESSAGES_TABLE,
          queries: [
            Query.equal("chats", chatId),
            Query.orderAsc("$createdAt"),
            Query.limit(200),
          ],
        });
        const serverMsgs = res.rows || [];
        setMessages((prev) => {
          const serverIds = new Set(serverMsgs.map((m) => m.$id));
          // Keep optimistic messages not yet confirmed by server
          const optimistic = prev.filter(
            (m) => pendingIds.current.has(m.$id) && !serverIds.has(m.$id),
          );
          return [...serverMsgs, ...optimistic];
        });
      } catch (e) {
        console.error(e);
      }
    };

    fetchMessages();
    pollRef.current = setInterval(fetchMessages, POLL_MS);

    return () => {
      clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [chatId, visible]);

  // Stop polling when window closes
  useEffect(() => {
    if (!visible) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [visible]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!text.trim() || !chatId || sending) return;
    const body = text.trim();
    const rowId = ID.unique();
    setText("");
    setSending(true);
    pendingIds.current.add(rowId);
    setMessages((prev) => [
      ...prev,
      {
        $id: rowId,
        text: body,
        sender: user.email,
        chats: chatId,
        $createdAt: new Date().toISOString(),
      },
    ]);
    try {
      await tablesDB.createRow({
        databaseId: DB_ID,
        tableId: MESSAGES_TABLE,
        rowId,
        data: { text: body, sender: user.email, chats: chatId },
      });
      await tablesDB.updateRow({
        databaseId: DB_ID,
        tableId: CHATS_TABLE,
        rowId: chatId,
        data: { lastMessageAt: new Date().toISOString() },
      });
      pendingIds.current.delete(rowId);
    } catch (e) {
      console.error(e);
      pendingIds.current.delete(rowId);
      setMessages((prev) => prev.filter((m) => m.$id !== rowId));
    } finally {
      setSending(false);
    }
  };

  const isMe = (msg) => msg.sender !== ADMIN_SENDER;

  return (
    <div className={`cw ${visible ? "cw--open" : ""}`} role="dialog">
      {/* Header */}
      <div className="cw__header">
        <div className="cw__header-left">
          <div className="cw__avatar">
            <img src={tbLogo} alt="TB" />
            <span className="cw__online-dot" />
          </div>
          <div>
            <p className="cw__brand">TrendBay Support</p>
            <p className="cw__status">
              <span
                className={`cw__status-dot${isOnline() ? "" : " cw__status-dot--offline"}`}
              />
              {isOnline()
                ? "Online · Replies Quickly"
                : "Offline · back at 8AM"}
            </p>
          </div>
        </div>
        <button className="cw__close" onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      {/* Body */}
      <div className="cw__body">
        {!user ? (
          <div className="cw__state">
            <div className="cw__state-icon">
              <MessageCircle size={36} color="#00A855" />
            </div>
            <p className="cw__state-title">Chat with us</p>
            <p className="cw__state-sub">
              Log in to start a conversation with our support team.
            </p>
            <a href="/login" className="cw__btn cw__btn--green">
              Log in to chat
            </a>
          </div>
        ) : loading ? (
          <div className="cw__state">
            <Loader size={28} color="#00A855" className="cw__spinner" />
            <p className="cw__state-sub">Connecting…</p>
          </div>
        ) : (
          <>
            <div className="cw__messages">
              {messages.length === 0 && (
                <div className="cw__welcome">
                  <div className="cw__welcome-avatar">
                    <img src={tbLogo} alt="TB" />
                  </div>
                  <p className="cw__welcome-title">
                    Hi{user?.name ? `, ${user.name.split(" ")[0]}` : ""}! 👋
                  </p>
                  <p className="cw__welcome-sub">How can we help you today?</p>
                </div>
              )}
              {messages.map((msg) => (
                <div
                  key={msg.$id}
                  className={`cw__msg ${isMe(msg) ? "cw__msg--me" : "cw__msg--them"}`}
                >
                  <div className="cw__bubble">{msg.text}</div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <div className="cw__input-bar">
              <input
                className="cw__input"
                type="text"
                placeholder="Type a message…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                disabled={sending}
              />
              <button
                className="cw__send"
                onClick={sendMessage}
                disabled={!text.trim() || sending}
              >
                {sending ? (
                  <Loader size={16} className="cw__spinner" />
                ) : (
                  <Send size={16} />
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
